import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiService from '../services/api';
import { Specialty as ApiSpecialty } from '../types';

export type SpecialtyType = string | null;

export interface Specialty {
  id: number;
  name: string;
  description: string;
  icon: string;
  color: string;
  is_active: boolean;
  has_reports?: boolean;
  has_refertazione: boolean;
  has_fatturazione: boolean;
}

// Mapping delle icone per specialistiche
const SPECIALTY_ICONS: Record<string, string> = {
  'cardiologia': '❤️',
  'dermatologia': '🧴',
  'medicina-interna': '🩺',
  'ortopedia': '🦴',
  'neurologia': '🧠',
  'pediatria': '👶',
  'ginecologia': '👩',
  'urologia': '🔬',
};

const SPECIALTY_COLORS: Record<string, string> = {
  'cardiologia': 'danger',
  'dermatologia': 'warning',
  'medicina-interna': 'info',
  'ortopedia': 'primary',
  'neurologia': 'dark',
  'pediatria': 'success',
  'ginecologia': 'pink',
  'urologia': 'secondary',
};

// Colori personalizzati per le specialità
const SPECIALTY_THEME_COLORS: Record<string, {
  primary: string;
  secondary: string;
  accent: string;
  background: string;
  text: string;
}> = {
  'cardiologia': {
    primary: '#dc3545',      // Rosso principale
    secondary: '#c82333',    // Rosso scuro
    accent: '#ff6b6b',       // Rosso chiaro
    background: '#fff5f5',   // Sfondo rosso chiarissimo
    text: '#721c24'          // Testo rosso scuro
  },
  'dermatologia': {
    primary: '#28a745',      // Verde principale
    secondary: '#1e7e34',    // Verde scuro
    accent: '#6bcf7f',       // Verde chiaro
    background: '#f8fff8',   // Sfondo verde chiarissimo
    text: '#155724'          // Testo verde scuro
  },
  'medicina-interna': {
    primary: '#17a2b8',
    secondary: '#138496',
    accent: '#74c0fc',
    background: '#f8f9fa',
    text: '#0c5460'
  }
};

interface SpecialtyContextType {
  selectedSpecialty: SpecialtyType;
  specialties: Specialty[];
  isLoading: boolean;
  setSelectedSpecialty: (specialty: SpecialtyType) => void;
  getSpecialtyInfo: (specialtyId: SpecialtyType) => Specialty | undefined;
  hasReportsEnabled: () => boolean;
  hasFatturazioneEnabled: () => boolean;
  clearSpecialty: () => void;
  getSpecialtyTheme: (specialtyId: SpecialtyType) => typeof SPECIALTY_THEME_COLORS[string] | undefined;
}

const SpecialtyContext = createContext<SpecialtyContextType | undefined>(undefined);

export const useSpecialty = () => {
  const context = useContext(SpecialtyContext);
  if (context === undefined) {
    throw new Error('useSpecialty must be used within a SpecialtyProvider');
  }
  return context;
};

interface SpecialtyProviderProps {
  children: ReactNode;
}

export const SpecialtyProvider: React.FC<SpecialtyProviderProps> = ({ children }) => {
  const [selectedSpecialty, setSelectedSpecialty] = useState<SpecialtyType>(() => {
    // Recupera la specialistica salvata dal localStorage
    return (localStorage.getItem('selectedSpecialty') as SpecialtyType) || null;
  });
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Carica le specialistiche dall'API
  useEffect(() => {
    const loadSpecialties = async () => {
      try {
        setIsLoading(true);
        const response = await apiService.getSpecialties();
        
        // Mappa i dati dall'API al formato interno
        const mappedSpecialties: Specialty[] = response.specialistiche.map((item: any) => ({
          id: item.specialistiche.id,
          name: item.specialistiche.name,
          description: `${item.specialistiche.name} - Specialistica medica`,
          icon: SPECIALTY_ICONS[item.specialistiche.name.toLowerCase()] || '🏥',
          color: SPECIALTY_COLORS[item.specialistiche.name.toLowerCase()] || 'secondary',
          is_active: item.is_active,
          has_reports: item.has_refertazione, // Usa il campo dall'API al livello principale
          has_refertazione: item.has_refertazione,
          has_fatturazione: item.has_fatturazione,
        }));
        
        setSpecialties(mappedSpecialties);
      } catch (error) {
        console.error('Errore nel caricamento delle specialistiche:', error);
        // In caso di errore, usa specialistiche di default
        setSpecialties([
          {
            id: 1,
            name: 'Cardiologia',
            description: 'Specialistica cardiologica',
            icon: '❤️',
            color: 'danger',
            is_active: true,
            has_reports: true,
            has_refertazione: true,
            has_fatturazione: true,
          }
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    loadSpecialties();
  }, []);

  const handleSetSelectedSpecialty = (specialty: SpecialtyType) => {
    setSelectedSpecialty(specialty);
    if (specialty) {
      localStorage.setItem('selectedSpecialty', specialty);
    } else {
      localStorage.removeItem('selectedSpecialty');
    }
  };

  const getSpecialtyInfo = (specialtyId: SpecialtyType): Specialty | undefined => {
    return specialties.find(s => s.id.toString() === specialtyId);
  };

  const hasReportsEnabled = (): boolean => {
    if (!selectedSpecialty) return false;
    const specialty = getSpecialtyInfo(selectedSpecialty);
    return specialty?.has_reports || false;
  };

  const hasFatturazioneEnabled = (): boolean => {
    if (!selectedSpecialty) return false;
    const specialty = getSpecialtyInfo(selectedSpecialty);
    return specialty?.has_fatturazione || false;
  };

  const clearSpecialty = () => {
    setSelectedSpecialty(null);
    localStorage.removeItem('selectedSpecialty');
  };

  const getSpecialtyTheme = (specialtyId: SpecialtyType) => {
    if (!specialtyId) return undefined;
    const specialty = getSpecialtyInfo(specialtyId);
    if (!specialty) return undefined;
    return SPECIALTY_THEME_COLORS[specialty.name.toLowerCase()];
  };

  const value: SpecialtyContextType = {
    selectedSpecialty,
    specialties,
    isLoading,
    setSelectedSpecialty: handleSetSelectedSpecialty,
    getSpecialtyInfo,
    hasReportsEnabled,
    hasFatturazioneEnabled,
    clearSpecialty,
    getSpecialtyTheme,
  };

  return (
    <SpecialtyContext.Provider value={value}>
      {children}
    </SpecialtyContext.Provider>
  );
};
